import type { GameMode, TeamName } from '@business-game/shared';

export type TeamColor = 'indigo' | 'emerald' | 'blue' | 'orange' | 'teal';

export interface TeamColorClasses {
  // Main colors
  primary: string; // bg-{color}-600
  primaryLight: string; // bg-{color}-100
  primaryDark: string; // bg-{color}-700
  primaryText: string; // text-{color}-600
  primaryTextLight: string; // text-{color}-700
  primaryBorder: string; // border-{color}-200
  primaryBorderLight: string; // border-{color}-100

  // Background blur
  blur: string; // bg-{color}-500
  blurLight: string; // bg-{color}-300 (for lighter blur)

  // Shadows
  shadow: string; // shadow-{color}-200

  // Progress bar
  progressFrom: string; // from-{color}-500
  progressTo: string; // to-purple-500 (keep purple for gradient)
}

const teamColorMap: Record<TeamName, TeamColor> = {
  team1: 'indigo',
  team2: 'emerald',
  team3: 'blue',
  team4: 'orange',
};

export function getTeamColor(teamName: TeamName | null, gameMode: GameMode | null = null): TeamColor {
  if (gameMode === 'leadlab' && (!teamName || teamName === 'team1')) {
    return 'teal';
  }

  if (!teamName) return 'indigo'; // Default
  return teamColorMap[teamName] || 'indigo';
}

export function getTeamColorClasses(
  teamName: TeamName | null,
  gameMode: GameMode | null = null
): TeamColorClasses {
  const color = getTeamColor(teamName, gameMode);

  // Use complete class names so Tailwind can detect them
  const colorMap: Record<TeamColor, TeamColorClasses> = {
    indigo: {
      primary: 'bg-indigo-600',
      primaryLight: 'bg-indigo-100',
      primaryDark: 'bg-indigo-700',
      primaryText: 'text-indigo-600',
      primaryTextLight: 'text-indigo-700',
      primaryBorder: 'border-indigo-200',
      primaryBorderLight: 'border-indigo-100',
      blur: 'bg-indigo-500',
      blurLight: 'bg-indigo-300',
      shadow: 'shadow-indigo-200',
      progressFrom: 'from-indigo-500',
      progressTo: 'to-purple-500',
    },
    emerald: {
      primary: 'bg-emerald-600',
      primaryLight: 'bg-emerald-100',
      primaryDark: 'bg-emerald-700',
      primaryText: 'text-emerald-600',
      primaryTextLight: 'text-emerald-700',
      primaryBorder: 'border-emerald-200',
      primaryBorderLight: 'border-emerald-100',
      blur: 'bg-emerald-500',
      blurLight: 'bg-emerald-300',
      shadow: 'shadow-emerald-200',
      progressFrom: 'from-emerald-500',
      progressTo: 'to-purple-500',
    },
    blue: {
      primary: 'bg-blue-600',
      primaryLight: 'bg-blue-100',
      primaryDark: 'bg-blue-700',
      primaryText: 'text-blue-600',
      primaryTextLight: 'text-blue-700',
      primaryBorder: 'border-blue-200',
      primaryBorderLight: 'border-blue-100',
      blur: 'bg-blue-500',
      blurLight: 'bg-blue-300',
      shadow: 'shadow-blue-200',
      progressFrom: 'from-blue-500',
      progressTo: 'to-purple-500',
    },
    orange: {
      primary: 'bg-orange-600',
      primaryLight: 'bg-orange-100',
      primaryDark: 'bg-orange-700',
      primaryText: 'text-orange-600',
      primaryTextLight: 'text-orange-700',
      primaryBorder: 'border-orange-200',
      primaryBorderLight: 'border-orange-100',
      blur: 'bg-orange-500',
      blurLight: 'bg-orange-300',
      shadow: 'shadow-orange-200',
      progressFrom: 'from-orange-500',
      progressTo: 'to-purple-500',
    },
    teal: {
      primary: 'bg-teal-600',
      primaryLight: 'bg-teal-100',
      primaryDark: 'bg-teal-700',
      primaryText: 'text-teal-600',
      primaryTextLight: 'text-teal-700',
      primaryBorder: 'border-teal-200',
      primaryBorderLight: 'border-teal-100',
      blur: 'bg-teal-500',
      blurLight: 'bg-teal-300',
      shadow: 'shadow-teal-200',
      progressFrom: 'from-teal-500',
      progressTo: 'to-cyan-500',
    },
  };

  return colorMap[color];
}

